import { useState, useEffect } from "react";
import { ActionItem, ActionItemStatus } from "../types/actionItems";
import { StatusFilter } from "./action-item/StatusFilter";
import { ActionItemCard } from "./action-item/ActionItemCard";
import { useAuth } from "../contexts/AuthContext";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { actionItemsService } from "../services/supabase/action-items";
import { Button } from "./ui/button";
import { Plus } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "./ui/sheet";
import { ActionItemForm } from "./action-item/ActionItemForm";
import { useToast } from "../hooks/use-toast";
import { supabase } from "../lib/supabase";
import { trackActionItemCreation } from "../utils/analytics";
import React from "react";

export function ActionItems() {
  const [selectedStatus, setSelectedStatus] = useState<ActionItemStatus | 'all' | 'open'>('open');
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [showForm, setShowForm] = useState(false);

  const { data: actionItems = [], isLoading, refetch } = useQuery({
    queryKey: ['actionItems', user?.id],
    queryFn: () => actionItemsService.getActionItems(user?.id || ''),
    enabled: !!user?.id,
  });

  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel('action-items-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'action_items',
          filter: `user_id=eq.${user.id}`,
        },
        () => {
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, refetch]);

  const sortedItems = [...actionItems].sort((a, b) => {
    const dateA = a.due_date ? new Date(a.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    const dateB = b.due_date ? new Date(b.due_date).getTime() : Number.MAX_SAFE_INTEGER;
    if (dateA !== dateB) {
      return dateA - dateB;
    }
    const createdAtA = new Date(a.created_at).getTime();
    const createdAtB = new Date(b.created_at).getTime();
    return createdAtB - createdAtA;
  });

  const filteredItems = React.useMemo(() => {
    if (selectedStatus === 'all') {
      return sortedItems;
    }
    
    if (selectedStatus === 'open') {
      return sortedItems.filter(item => 
        ['todo', 'in_progress', 'on_hold'].includes(item.status)
      );
    }
    
    return sortedItems.filter(item => item.status === selectedStatus);
  }, [sortedItems, selectedStatus]);

  const counts = {
    all: actionItems.length,
    open: actionItems.filter(item => ['todo', 'in_progress', 'on_hold'].includes(item.status)).length,
    todo: actionItems.filter(item => item.status === 'todo').length,
    in_progress: actionItems.filter(item => item.status === 'in_progress').length,
    on_hold: actionItems.filter(item => item.status === 'on_hold').length,
    done: actionItems.filter(item => item.status === 'done').length,
  };

  const handleCreateActionItem = async (formData: any) => {
    try {
      if (!user?.id) return;
      const newItem = await actionItemsService.createActionItem(user.id, formData);
      trackActionItemCreation(newItem.id);
      toast({
        title: "Success",
        description: "Action item created successfully",
      });
      setShowForm(false);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to create action item",
        variant: "destructive",
      });
    }
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4 pl-4 pt-4 pr-4">
        <h1 className="text-2xl font-bold">Action Items</h1>
        <Sheet open={showForm} onOpenChange={setShowForm}>
          <SheetTrigger asChild>
            <Button size="icon" className="rounded-full">
              <Plus className="h-4 w-4" />
            </Button>
          </SheetTrigger>
          <SheetContent>
            <SheetHeader>
              <SheetTitle>New Action Item</SheetTitle>
            </SheetHeader>
            <div className="mt-6">
              <ActionItemForm
                onSubmit={handleCreateActionItem}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </SheetContent>
        </Sheet>
      </div>

      <StatusFilter
        value={selectedStatus}
        onChange={setSelectedStatus}
        counts={counts}
      />

      <div className="grid gap-4">
        {filteredItems.map((item) => (
          <ActionItemCard
            key={item.id}
            item={item}
            onClick={() => navigate(`/app/action-items/${item.id}`)}
            meetingTitle={item.meeting?.title}
          />
        ))}
      </div>
    </div>
  );
}